import { useState, useCallback } from 'react';
import { termsService, TermsDocument, PendingTermsResponse } from '../services/terms';

// Type for terms from login response (may have slightly different structure)
export interface LoginTermsDocument {
  id: string;
  type: string;
  version: string;
  title: string;
  content_url?: string;
  effective_date: string;
  is_active: boolean;
}

interface UseTermsAcceptanceReturn {
  hasPendingTerms: boolean;
  pendingTerms: TermsDocument[];
  isCheckingTerms: boolean;
  isAcceptingTerms: boolean;
  termsError: string | null;
  termsContent: string | null;
  isLoadingContent: boolean;
  checkPendingTerms: () => Promise<PendingTermsResponse | null>;
  setPendingTermsFromLogin: (terms: LoginTermsDocument[] | undefined, hasPending: boolean | undefined) => void;
  acceptTerms: (termsId: string) => Promise<boolean>;
  loadTermsContent: (type: string) => Promise<void>;
  clearTermsState: () => void;
  clearError: () => void;
}

export const useTermsAcceptance = (): UseTermsAcceptanceReturn => {
  const [pendingTerms, setPendingTerms] = useState<TermsDocument[]>([]);
  const [hasPendingTerms, setHasPendingTerms] = useState(false);
  const [isCheckingTerms, setIsCheckingTerms] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [termsContent, setTermsContent] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const checkPendingTerms = useCallback(async (): Promise<PendingTermsResponse | null> => {
    setIsCheckingTerms(true);
    setTermsError(null);

    try {
      const response = await termsService.getPendingTerms();
      setPendingTerms(response.pending_terms || []);
      setHasPendingTerms(response.has_pending);
      return response;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check pending terms';
      setTermsError(message);
      console.error('Terms check error:', message);
      return null;
    } finally {
      setIsCheckingTerms(false);
    }
  }, []);

  const loadTermsContent = useCallback(async (type: string): Promise<void> => {
    setIsLoadingContent(true);
    setTermsError(null);

    try {
      const content = await termsService.getTermsContent(type);
      setTermsContent(content);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load terms content';
      setTermsError(message);
      console.error('Terms content error:', message);
    } finally {
      setIsLoadingContent(false);
    }
  }, []);

  const acceptTerms = useCallback(async (termsId: string): Promise<boolean> => {
    setIsAcceptingTerms(true);
    setTermsError(null);

    try {
      await termsService.acceptTerms(termsId);

      // Remove the accepted terms from the pending list
      setPendingTerms((prev) => {
        const updated = prev.filter((t) => t.id !== termsId);
        setHasPendingTerms(updated.length > 0);
        return updated;
      });

      // Clear content after acceptance so next term can load fresh
      setTermsContent(null);

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept terms';
      setTermsError(message);
      console.error('Terms accept error:', message);
      return false;
    } finally {
      setIsAcceptingTerms(false);
    }
  }, []);

  const clearTermsState = useCallback(() => {
    setPendingTerms([]);
    setHasPendingTerms(false);
    setTermsError(null);
    setTermsContent(null);
  }, []);

  const clearError = useCallback(() => {
    setTermsError(null);
  }, []);

  // Set pending terms directly from login response (avoids separate API call)
  const setPendingTermsFromLogin = useCallback((terms: LoginTermsDocument[] | undefined, hasPending: boolean | undefined) => {
    if (terms && terms.length > 0) {
      // Convert LoginTermsDocument to TermsDocument format
      const convertedTerms: TermsDocument[] = terms.map((t) => ({
        id: t.id,
        type: t.type,
        version: t.version,
        title: t.title,
        content_url: t.content_url,
        effective_date: t.effective_date,
        is_active: t.is_active,
        created_at: t.effective_date, // Use effective_date as fallback
        updated_at: t.effective_date,
      }));
      setPendingTerms(convertedTerms);
      setHasPendingTerms(true);
    } else {
      setPendingTerms([]);
      setHasPendingTerms(hasPending ?? false);
    }
  }, []);

  return {
    hasPendingTerms,
    pendingTerms,
    isCheckingTerms,
    isAcceptingTerms,
    termsError,
    termsContent,
    isLoadingContent,
    checkPendingTerms,
    setPendingTermsFromLogin,
    acceptTerms,
    loadTermsContent,
    clearTermsState,
    clearError,
  };
};
